import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type {
    AnswerStatus,
    FinalQuizAnswerRequest,
    FinalQuizInfoResponse,
    FinalQuizStatus,
    FinalQuizUserQuestionsResponse,
    QuizNotification,
} from '@routes/sidebar/learning/___pages___/learning-overview/api/finalQuiz.api.ts';
import {
    getApiFinalQuizData,
    postApiFinalQuizAnswer,
    postApiFinalQuizFinish,
    postApiFinalQuizStart,
    postResetFinalQuizData,
} from '@routes/sidebar/learning/___pages___/learning-overview/api/finalQuiz.api.ts';
import {
    getLearningOverview,
    resetPathway,
    resetPathwayElementsProgress,
} from '@routes/sidebar/learning/___pages___/learning-overview/model/learningOverview.slice.ts';
import { COMMON_QUIZ_STATUS } from '@routes/sidebar/learning/___pages___/learning-overview/ui/overview/quiz/___shared___/constants.ts';
import {
    applyAttemptsLeft,
    isNoAttemptsLeftQuiz,
    removeQuestionFromSkippedOnes,
} from '@routes/sidebar/learning/___pages___/learning-overview/ui/overview/quiz/___shared___/helpers.ts';
import { extractError } from '@utils/slice.ts';

export interface LearningFinalQuizSlice {
    finalQuizData: FinalQuizUserQuestionsResponse | null;
    finalQuizIntroData: FinalQuizInfoResponse | null;
    error: Record<string, any> | null;
    skippedQuestions: Record<number, number[]>;
    currentQuestionId: number | null;
    isAnyAnswerProvided: boolean;
    checkedAnswers: Record<number, number[]>;
    correctAnswers: Record<number, number[]>;
    answeredQuestions: number[];
    quizQuestionAnswerStatuses: Record<number, AnswerStatus>;
    quizFinishData: FinalQuizInfoResponse | null;
    finalQuizAnswers: FinalQuizAnswerRequest[];
    toggleTimerInFinalQuiz: boolean;
    showTimesUpModal: boolean;
    showUnansweredQuestionsModal: boolean;
    currentFinalQuizStatus: FinalQuizStatus | undefined;
    attemptsLeft: number;
    unAnsweredQuestionsAnfLifeTime: {
        remainingTimeSeconds: number;
        unansweredQuestionNumbers: number[];
    };
    quizNotification: QuizNotification | undefined;
    isFinalQuizFinished: boolean;
    isFinalQuizBlockedAfterNoAttempts: boolean;
    skipLocalPathwayReset: boolean;
    showSkipQuestionsNumbersBanner: boolean;

    isFetchFinalQuizDataPending: boolean;
    isStartFinalQuizPending: boolean;
    isPostAnswerFinalQuizPending: boolean;
    isFinishFinalQuizPending: boolean;
    isResetFinalQuizDataPending: boolean;
}

const initialState: LearningFinalQuizSlice = {
    finalQuizData: null,
    finalQuizIntroData: null,
    error: null,
    skippedQuestions: {},
    currentQuestionId: null,
    isAnyAnswerProvided: false,
    checkedAnswers: {},
    correctAnswers: {},
    answeredQuestions: [],
    quizQuestionAnswerStatuses: {},
    quizFinishData: null,
    finalQuizAnswers: [],
    toggleTimerInFinalQuiz: false,
    showTimesUpModal: false,
    showUnansweredQuestionsModal: false,
    currentFinalQuizStatus: undefined,
    attemptsLeft: 0,
    unAnsweredQuestionsAnfLifeTime: {
        remainingTimeSeconds: 0,
        unansweredQuestionNumbers: [],
    },
    quizNotification: undefined,
    isFinalQuizFinished: false,
    isFinalQuizBlockedAfterNoAttempts: false,
    skipLocalPathwayReset: false,
    showSkipQuestionsNumbersBanner: false,

    isFetchFinalQuizDataPending: true,
    isStartFinalQuizPending: false,
    isPostAnswerFinalQuizPending: false,
    isFinishFinalQuizPending: false,
    isResetFinalQuizDataPending: false,
};

export const fetchFinalQuizData = createAsyncThunk<
    FinalQuizInfoResponse,
    { pathwayId: string; quizId: number },
    { rejectValue: any }
>('learningFinalQuiz/fetchFinalQuizData', ({ pathwayId, quizId }, { rejectWithValue }) =>
    getApiFinalQuizData(pathwayId, quizId)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const startFinalQuiz = createAsyncThunk<
    FinalQuizUserQuestionsResponse,
    { pathwayId: string; quizId: number },
    { rejectValue: any }
>('learningFinalQuiz/startFinalQuiz', ({ pathwayId, quizId }, { rejectWithValue }) =>
    postApiFinalQuizStart(pathwayId, quizId)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const postAnswerFinalQuiz = createAsyncThunk<
    any,
    {
        payload: FinalQuizAnswerRequest;
        quizId: number;
        pathwayId: string;
    },
    { rejectValue: any }
>('learningFinalQuiz/postApiFinalQuizAnswer', ({ payload, quizId, pathwayId }, { rejectWithValue }) =>
    postApiFinalQuizAnswer(payload, quizId, pathwayId)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const finishFinalQuizAndUpdatePathwayConditionally = createAsyncThunk<
    FinalQuizInfoResponse,
    { pathwayId: string; quizId: number },
    { rejectValue: any }
>(
    'learningFinalQuiz/finishFinalQuizAndUpdatePathwayConditionally',
    ({ pathwayId, quizId }, { dispatch, rejectWithValue }) => {
        dispatch(setIsFinalQuizFinished(true));

        return postApiFinalQuizFinish(pathwayId, quizId)
            .then((res) => {
                // Centralized pathway refresh logic after final quiz completion.
                // Multiple flows (4 different entry points) can finish the quiz, so
                // keeping this update here avoids duplicating the same behavior (DRY).
                if (isNoAttemptsLeftQuiz(res.quizStatus, res.quizNotification)) {
                    dispatch(setSkipLocalPathwayReset(false));
                    dispatch(setIsFinalQuizBlockedAfterNoAttempts(true));
                    dispatch(resetPathwayElementsProgress());
                }

                // update pathway state after final quiz finished successfully
                if (res.quizStatus === COMMON_QUIZ_STATUS.PASSED) {
                    return dispatch(getLearningOverview(Number(pathwayId))).then(() => res);
                }

                return res;
            })
            .catch((err) => rejectWithValue(err));
    },
);

export const resetFinalQuizData = createAsyncThunk<
    FinalQuizInfoResponse,
    { pathwayId: string; quizId: number },
    { rejectValue: any }
>('learningFinalQuiz/postResetFinalQuizData', ({ pathwayId, quizId }, { rejectWithValue }) =>
    postResetFinalQuizData(pathwayId, quizId)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

const learningFinalQuizSlice = createSlice({
    name: 'learningFinalQuiz',
    initialState,
    reducers: {
        setSkippedQuestions(state, action: PayloadAction<{ quizId: number; questionId: number }>) {
            const { quizId, questionId } = action.payload;
            const list = state.skippedQuestions[quizId] ?? (state.skippedQuestions[quizId] = []);
            if (!list.includes(questionId)) {
                list.push(questionId);
                state.showSkipQuestionsNumbersBanner = true;
            }
        },
        clearAllSkippedQuestions(state) {
            state.skippedQuestions = {};
        },
        setIsAnyAnswerProvided(state, action: PayloadAction<boolean>) {
            state.isAnyAnswerProvided = action.payload;
        },
        setCurrentQuestionId(state, action: PayloadAction<number | null>) {
            state.currentQuestionId = action.payload;
        },
        setCheckedAnswers(state, action: PayloadAction<{ questionId: number; answers: number[] }>) {
            const { questionId, answers } = action.payload;
            state.checkedAnswers[questionId] = answers;
        },
        setFinalQuizAnswers(state, action: PayloadAction<{ questionId: number; answersIds: number[] }>) {
            const { questionId, answersIds } = action.payload;
            const existing = state.finalQuizAnswers.find((answer) => answer.questionId === questionId);

            if (existing) {
                existing.answersIds = answersIds;
            } else {
                state.finalQuizAnswers.push({ questionId, answersIds });
            }

            if (!state.answeredQuestions.includes(questionId)) {
                state.answeredQuestions.push(questionId);
            }
        },
        setToggleTimerInFinalQuiz(state, action: PayloadAction<boolean>) {
            state.toggleTimerInFinalQuiz = action.payload;
        },
        setShowTimesUpModal(state, action: PayloadAction<boolean>) {
            state.showTimesUpModal = action.payload;
        },
        setShowUnansweredQuestionsModal(state, action: PayloadAction<boolean>) {
            state.showUnansweredQuestionsModal = action.payload;
        },
        setUnAnsweredQuestionsAnfLifeTime(
            state,
            action: PayloadAction<Partial<LearningFinalQuizSlice['unAnsweredQuestionsAnfLifeTime']>>,
        ) {
            state.unAnsweredQuestionsAnfLifeTime = { ...state.unAnsweredQuestionsAnfLifeTime, ...action.payload };
        },
        clearFinalQuizData(state) {
            state.finalQuizAnswers = [];
            state.currentQuestionId = null;
            state.answeredQuestions = [];
            state.checkedAnswers = {};
            state.skippedQuestions = {};
            state.unAnsweredQuestionsAnfLifeTime = {
                remainingTimeSeconds: 0,
                unansweredQuestionNumbers: [],
            };
        },
        setIsFinalQuizFinished(state, action: PayloadAction<boolean>) {
            state.isFinalQuizFinished = action.payload;
        },
        setIsFinalQuizBlockedAfterNoAttempts(state, action: PayloadAction<boolean>) {
            state.isFinalQuizBlockedAfterNoAttempts = action.payload;
        },
        setSkipLocalPathwayReset(state, action: PayloadAction<boolean>) {
            state.skipLocalPathwayReset = action.payload;
        },
        showSkipQuestionsNumbersBanner(state, action: PayloadAction<boolean>) {
            state.showSkipQuestionsNumbersBanner = action.payload;
        },
        clearFinalQuizFinishData(state) {
            state.quizFinishData = null;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(startFinalQuiz.pending, (state) => {
            state.error = null;
            state.isStartFinalQuizPending = true;
            state.currentFinalQuizStatus = undefined;
        });
        builder.addCase(startFinalQuiz.fulfilled, (state, action) => {
            state.finalQuizData = action.payload;
            state.currentQuestionId = action.payload.finalQuizQuestion[0]?.id ?? null;
            state.currentFinalQuizStatus = action.payload.finalQuizDescription.quizStatus;
            state.toggleTimerInFinalQuiz = true;
            state.showTimesUpModal = false;
            state.isStartFinalQuizPending = false;
        });
        builder.addCase(startFinalQuiz.rejected, (state, action) => {
            state.error = extractError(action);
            state.isStartFinalQuizPending = false;
        });

        builder.addCase(fetchFinalQuizData.pending, (state) => {
            state.error = null;
            state.isFetchFinalQuizDataPending = true;
            state.currentFinalQuizStatus = undefined;
            state.attemptsLeft = 0;
            state.quizNotification = undefined;
        });
        builder.addCase(fetchFinalQuizData.fulfilled, (state, action) => {
            state.finalQuizIntroData = action.payload;
            state.quizFinishData = action.payload;
            state.error = null;
            state.currentFinalQuizStatus = action.payload.quizStatus;
            applyAttemptsLeft(state, action.payload);
            state.quizNotification = action.payload.quizNotification;
            state.isFinalQuizBlockedAfterNoAttempts = isNoAttemptsLeftQuiz(
                action.payload.quizStatus,
                action.payload.quizNotification,
            );
            state.isFinalQuizFinished = false;
            state.isFetchFinalQuizDataPending = false;
        });
        builder.addCase(fetchFinalQuizData.rejected, (state, action) => {
            state.error = extractError(action);
            state.isFetchFinalQuizDataPending = false;
        });

        builder.addCase(postAnswerFinalQuiz.pending, (state) => {
            state.error = null;
            state.isPostAnswerFinalQuizPending = true;
        });
        builder.addCase(postAnswerFinalQuiz.fulfilled, (state, action) => {
            state.showSkipQuestionsNumbersBanner = false;
            const questionId = action.meta.arg.payload.questionId;
            const quizId = action.meta.arg.quizId;
            removeQuestionFromSkippedOnes(state, questionId, quizId);
            state.isPostAnswerFinalQuizPending = false;
        });
        builder.addCase(postAnswerFinalQuiz.rejected, (state, action) => {
            state.error = extractError(action);
            state.isPostAnswerFinalQuizPending = false;
        });

        builder.addCase(finishFinalQuizAndUpdatePathwayConditionally.pending, (state) => {
            state.error = null;
            state.isFinishFinalQuizPending = true;
        });
        builder.addCase(finishFinalQuizAndUpdatePathwayConditionally.fulfilled, (state, action) => {
            state.quizFinishData = action.payload;
            state.currentFinalQuizStatus = action.payload.quizStatus;
            applyAttemptsLeft(state, action.payload);
            state.quizNotification = action.payload.quizNotification;
            state.isFinalQuizBlockedAfterNoAttempts = isNoAttemptsLeftQuiz(
                action.payload.quizStatus,
                action.payload.quizNotification,
            );
            if (isNoAttemptsLeftQuiz(action.payload.quizStatus, action.payload.quizNotification)) {
                state.skipLocalPathwayReset = false;
            }
            state.isFinishFinalQuizPending = false;
        });
        builder.addCase(finishFinalQuizAndUpdatePathwayConditionally.rejected, (state, action) => {
            state.error = extractError(action);
            state.isFinishFinalQuizPending = false;
        });

        builder.addCase(resetFinalQuizData.pending, (state) => {
            state.error = null;
            state.isResetFinalQuizDataPending = true;
        });
        builder.addCase(resetFinalQuizData.fulfilled, (state) => {
            state.isResetFinalQuizDataPending = false;
        });
        builder.addCase(resetFinalQuizData.rejected, (state, action) => {
            state.error = extractError(action);
            state.isResetFinalQuizDataPending = false;
        });

        builder.addCase(resetPathway.fulfilled, (state) => {
            state.isFinalQuizBlockedAfterNoAttempts = false;
            state.skipLocalPathwayReset = false;
        });
    },
});

export const {
    setSkippedQuestions,
    setIsAnyAnswerProvided,
    setCurrentQuestionId,
    setCheckedAnswers,
    clearAllSkippedQuestions,
    setFinalQuizAnswers,
    setToggleTimerInFinalQuiz,
    setShowTimesUpModal,
    setShowUnansweredQuestionsModal,
    setUnAnsweredQuestionsAnfLifeTime,
    clearFinalQuizData,
    setIsFinalQuizFinished,
    setIsFinalQuizBlockedAfterNoAttempts,
    setSkipLocalPathwayReset,
    showSkipQuestionsNumbersBanner,
    clearFinalQuizFinishData,
} = learningFinalQuizSlice.actions;
export default learningFinalQuizSlice.reducer;
